// Live build calls (spec 2026-10-08 R8/R10): the active-build lookup and continue/redo of a paused build.
// Ids are validated before they reach a URL; mw re-checks ownership. Same mw root rule as BuildChat.
import { authedFetch } from "../../../common/http_service";
import { JOB } from "./buildMode";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const root = () =>
  String(window.ideaLibraryRootUrl || "")
    .replace(/\/+$/, "")
    .replace(/\/api$/, "");

// GET /course-build/active → the running or paused build of this course, else null (mw: empty body = none).
// Returns: { jobId, draftTrainingId, ... } if a build exists, null if no build (404/403/empty),
// or undefined if the probe itself failed (network error, non-JSON response, 5xx).
// quiet: 404/403 is "no build", not a toast; other errors (5xx, network) are silent but NOT the same as "no build".
export async function fetchActiveBuild(trainingId) {
  if (!UUID.test(String(trainingId || ""))) return null;
  try {
    const r = await authedFetch(
      `${root()}/api/course-build/active?trainingId=${encodeURIComponent(trainingId)}`,
      {},
      { quiet: true }
    );
    if (!r.ok) return null; // 404, 403, etc. = no build
    const text = await r.text();
    const build = text ? JSON.parse(text) : null;
    return build && typeof build.jobId === "string" ? build : null;
  } catch (e) {
    return undefined; // network error, non-JSON parse, or other probe failure
  }
}

// GET doc-planner/reasoning-agent/options?kind=agentFlow → { flows: [{ id, name, whenToUse, soon? }] }: the console recipe
// wizard's flow cards (same controller, headers and auth as the build chat's /state). Never throws: [] = no flows panel.
export async function fetchAgentFlows(draftId, lng) {
  if (!UUID.test(String(draftId || ""))) return [];
  try {
    const r = await authedFetch(
      `${root()}/api/doc-planner/reasoning-agent/options?kind=agentFlow&threadId=${encodeURIComponent(`reasoning-${draftId}`)}`,
      { headers: { "X-Aidea-Training-Id": draftId, ...(lng ? { "X-Aidea-Locale": lng } : {}) } },
      { quiet: true }
    );
    if (!r || !r.ok) return [];
    const data = await r.json();
    return (Array.isArray(data && data.flows) ? data.flows : []).filter(
      (f) =>
        f &&
        !f.soon &&
        f.id !== "auto" &&
        typeof f.name === "string" &&
        typeof f.whenToUse === "string"
    );
  } catch (e) {
    return [];
  }
}

// POST /course-build/:id/stop → { jobId, queued }. Scenes already built stay; the build then ends with course_build_done {stopped:"user"}.
// Throws on any refusal (409 = not running any more); quiet: the stop control shows the failure itself.
export async function postBuildStop(trainingId) {
  if (!UUID.test(String(trainingId || ""))) throw new Error("invalid training id");
  const r = await authedFetch(
    `${root()}/api/course-build/${encodeURIComponent(trainingId)}/stop`,
    { method: "POST" },
    { quiet: true }
  );
  if (!r.ok) throw Object.assign(new Error(`stop failed: ${r.status}`), { status: r.status });
  return r.json();
}

// POST /course-build/:id/continue → { jobId, room } of the new job. Throws on any refusal (409 = not paused any more);
// the error carries the HTTP status. quiet: the pause card shows the failure, no toast or auth modal (401 still renews).
export async function postBuildContinue(trainingId, action, note, pick) {
  if (!UUID.test(String(trainingId || ""))) throw new Error("invalid training id");
  const r = await authedFetch(
    `${root()}/api/course-build/${encodeURIComponent(trainingId)}/continue`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        note
          ? {
              action,
              brief: String(note).slice(0, 1000),
              ...(pick && pick.format ? { format: pick.format, variant: pick.variant } : {}),
              ...(pick && pick.style ? { style: pick.style } : {}),
            }
          : { action }
      ),
    },
    { quiet: true }
  );
  if (!r.ok) throw Object.assign(new Error(`continue failed: ${r.status}`), { status: r.status });
  const data = await r.json();
  if (!data || typeof data.jobId !== "string" || !JOB.test(data.jobId))
    throw new Error("continue failed: no job");
  return data;
}

// POST /course-build/:id/chat → { intent: question|redo|continue, reply, note?, options? }. Never rebuilds (mw only classifies);
// throws on any failure so the caller falls back to explicit choices.
export async function postBuildChat(trainingId, message, extra) {
  if (!UUID.test(String(trainingId || ""))) throw new Error("invalid training id");
  const r = await authedFetch(
    `${root()}/api/course-build/${encodeURIComponent(trainingId)}/chat`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: String(message).slice(0, 1000),
        ...(extra && extra.lng ? { lng: String(extra.lng).slice(0, 10) } : {}),
        ...(extra && extra.exclude && extra.exclude.length
          ? { exclude: extra.exclude.slice(0, 30) }
          : {}),
      }),
    },
    { quiet: true }
  );
  if (!r.ok) throw Object.assign(new Error(`chat failed: ${r.status}`), { status: r.status });
  const data = await r.json();
  if (
    !data ||
    !["question", "redo", "continue"].includes(data.intent) ||
    typeof data.reply !== "string"
  )
    throw new Error("chat failed: bad reply");
  return data;
}

// GET /course-build/:id/pick-options?kind=style|format → { styles: [{ styleId, styleSource, label, previewUrl }] } | { formats: [{ slug, name, whenToUse, variants }] }
// (the See-all popup). Throws on any failure; the popup shows an error line.
export async function fetchPickOptions(trainingId, kind) {
  if (!UUID.test(String(trainingId || "")) || (kind !== "style" && kind !== "format"))
    throw new Error("invalid pick request");
  const r = await authedFetch(
    `${root()}/api/course-build/${encodeURIComponent(trainingId)}/pick-options?kind=${kind}`,
    {},
    { quiet: true }
  );
  if (!r.ok)
    throw Object.assign(new Error(`pick-options failed: ${r.status}`), { status: r.status });
  const data = await r.json();
  const list = kind === "style" ? data && data.styles : data && data.formats;
  if (!Array.isArray(list)) throw new Error("pick-options failed: bad reply");
  return list;
}
